import type { LineDashPattern } from './line-dash-pattern.js'
import type { MapInteractionsConfig } from './map-interactions.js'
import type { LatLng, LatLngBounds } from './places.js'

export type GeoJsonFeatureCollection = {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    geometry: { type: string; coordinates: unknown }
    properties?: Record<string, unknown>
  }>
}

/**
 * Visual + popup hints for map providers. Nested under **`properties.map`** on each GeoJSON feature.
 * Domain-specific fields (stops, geofences, etc.) stay outside `map`; providers read only `map`.
 *
 * **LineString / MultiLineString:** each feature can carry its own `map` (colors, `lineAntPath`, etc.).
 * Different lines in the same `FeatureCollection` may use different ant-path palettes. Leaflet applies
 * `map` per feature. Mapbox encodes resolved ant-path paint into per-feature props for data-driven layers;
 * if several ant paths set different {@link MapFeatureMapStyle.lineAntPathDelay}, the dash animation uses
 * the delay of the **first** ant-path feature in collection order.
 */
export type MapFeatureMapStyle = {
  fill?: string
  fillOpacity?: number
  stroke?: string
  strokeOpacity?: number
  strokeWidth?: number
  lineColor?: string
  lineWidth?: number
  lineOpacity?: number
  /**
   * Optional dash pattern for `LineString` / `MultiLineString` (not used with {@link MapFeatureMapStyle.lineAntPath}).
   * Canonical format: {@link LineDashPattern} — lengths are **multiples of** {@link MapFeatureMapStyle.lineWidth}
   * (same as Mapbox `line-dasharray`). Leaflet applies `lineDashPatternToPixelSegments` for `dashArray`.
   */
  lineDashArray?: LineDashPattern
  /**
   * When true, `LineString` / `MultiLineString` uses animated ant path (movement direction).
   * Leaflet: `leaflet-ant-path`. Mapbox: two `line` layers + `line-dasharray` animation.
   */
  lineAntPath?: boolean
  /** Ant-path period in ms (Leaflet `delay`; Mapbox one full dash cycle). Default 4000. */
  lineAntPathDelay?: number
  /** Ant-path secondary color (Leaflet `pulseColor`; Mapbox underlay). Default `#000000`. */
  lineAntPathPulseColor?: string
  /**
   * When true, a 2-vertex `LineString` is drawn as a great-circle arc (not a planar chord).
   * Uses the same stroke fields as a normal line. Mutually exclusive with {@link MapFeatureMapStyle.lineAntPath}
   * — if both are set, `lineGeodesic` wins.
   */
  lineGeodesic?: boolean
  /** Arc sampling density. Default 50. */
  lineGeodesicSteps?: number
  /**
   * **Point / MultiPoint** — small circle markers (same idea as device history track points on `apps/app` maps).
   * When omitted, providers use neutral defaults.
   */
  pointRadius?: number
  pointFill?: string
  pointStroke?: string
  pointStrokeWidth?: number
  /**
   * Leaflet: when true, point circles use a pane below HTML markers (z-index &lt; marker pane ~600) so they do not
   * cover current-position pins or **numbered stop markers** on the shipment operational map. Default false —
   * uses a raised pane so circles sit above generic overlays.
   */
  pointCircleBelowHtmlMarkers?: boolean
  /**
   * Plain text (escaped) or HTML when {@link MapFeatureMapStyle.popupHtml} is true.
   * @deprecated Use {@link MapProvider.setInteractions} and `surface` instead.
   */
  popup?: string
  /**
   * When true, `popup` is inserted as HTML into the map popup.
   * @deprecated Use {@link MapProvider.setInteractions} and `surface` instead.
   */
  popupHtml?: boolean
}

export function getMapStyleFromFeatureProperties(
  properties: Record<string, unknown> | undefined
): MapFeatureMapStyle {
  if (!properties || typeof properties !== 'object') return {}
  const m = (properties as { map?: Partial<MapFeatureMapStyle> }).map
  if (m && typeof m === 'object' && !Array.isArray(m)) {
    return { ...m }
  }
  return {}
}

/**
 * Raster icon — same layout on Leaflet (`L.Icon`) and Mapbox (custom `Marker` element).
 * Defaults match `apps/app` `utils/map/new-markers.js` (GPS / fix-type SVGs).
 */
export type MapMarkerIconOptions = {
  url: string
  /** Width and height in CSS pixels. */
  size?: [number, number]
  /**
   * The point in the image that corresponds to the geographic position, as `[x, y]` from the image’s top-left.
   * Matches Leaflet `iconAnchor`. Default centers horizontally and pins to the bottom (`[w/2, h]`).
   */
  anchor?: [number, number]
  shadowUrl?: string
  shadowSize?: [number, number]
  /** Anchor of the shadow image (Leaflet `shadowAnchor`). */
  shadowAnchor?: [number, number]
  /** Offset of the popup relative to the icon anchor (Leaflet `popupAnchor`). */
  popupAnchor?: [number, number]
}

/**
 * Custom circle marker (HTML div on Leaflet and Mapbox) — **not** Leaflet’s default image pin / `L.marker` without icon.
 *
 * - **Without `label`:** small solid dot (fill + light border) — same code path as with label.
 * - **With `label`:** labs-style ring + centered text (digit/letter).
 * - **`pulse`:** optional CSS ripple (`box-shadow`) on the disc; providers inject shared `@keyframes` once.
 */
export type MapMarkerCircleStyle = {
  kind: 'circle'
  /** Fill of the circle. */
  fillColor?: string
  /** Stroke around the circle. */
  borderColor?: string
  /** Centered text; omit for a plain colored dot. */
  label?: string
  /** Diameter in CSS pixels. Default: 28 when `label` is set, 22 otherwise. */
  sizePx?: number
  fontSizePx?: number
  textColor?: string
  /**
   * When true, the disc animates a soft pulse ring (shared CSS). Extra margin is reserved so the ring is not clipped.
   */
  pulse?: boolean
}

/**
 * Built-in map pin.
 *
 * - **Without `color`:** same as Leaflet `L.marker(latlng).addTo(map)` and Mapbox `new mapboxgl.Marker().setLngLat(...).addTo(map)` — each engine’s default pin.
 * - **With `color`:** Mapbox `Marker({ color })`; Leaflet uses an SVG pin with that fill.
 */
export type MapMarkerPinStyle = {
  kind: 'pin'
  /**
   * When set, Mapbox passes `color` to `Marker`; Leaflet draws a colored SVG pin.
   * Omit for the library default pin (not the same pixels in both engines, but the usual “stock” marker).
   */
  color?: string
}

export type MapMarkerStyle =
  | MapMarkerCircleStyle
  | MapMarkerPinStyle
  | { kind: 'icon'; icon: MapMarkerIconOptions }

/** Styling for a geodesic segment after this marker in an {@link MapMarkerArc} chain. */
export type MapMarkerArcStyle = {
  lineColor?: string
  lineWidth?: number
  lineOpacity?: number
  lineGeodesicSteps?: number
}

/**
 * When set, the marker participates in a geodesic arc chain within its layer.
 * Markers sharing the same {@link MapMarkerArc.groupId} are connected by consecutive {@link MapMarkerArc.order}.
 */
export type MapMarkerArc = {
  groupId: string
  /** Position in the chain (0-based). */
  order: number
  /** Segment style after this point; omitted fields fall back to marker appearance, then provider defaults. */
  style?: MapMarkerArcStyle
  /** Hit payload for arc interactions; lowest {@link order} in the group wins. */
  payload?: Record<string, unknown>
}

/**
 * One map marker: stable `id`, `position`, discriminated `style`, optional popup.
 */
export type MapMarker = {
  id: string
  position: LatLng
  style: MapMarkerStyle
  /**
   * When set, providers draw a geodesic accuracy circle (meters) around {@link position}.
   * The app decides when to set this (e.g. cell/wifi and high uncertainty); providers do not apply fix/threshold rules.
   * Omitted or non-positive: no ring. Clustered marker layers may omit rings until a follow-up supports unclustered-only rings.
   */
  accuracyRadiusMeters?: number
  /**
   * Color for proportional donut cluster slices. Overrides auto-detection from {@link MapMarker.style}.
   * `kind: 'icon'` has no default — set explicitly to include a slice.
   */
  clusterColor?: string
  /**
   * When several markers represent one logical entity (e.g. origin + destination for one shipment),
   * set the same id on each — donut cluster center count and slices dedupe by this key.
   */
  clusterGroupId?: string
  /**
   * Optional arc-chain membership. Providers connect markers with the same {@link MapMarkerArc.groupId}
   * using cluster-aware display positions.
   */
  arc?: MapMarkerArc
  /**
   * Popup body; escaped as plain text unless {@link MapMarker.popupHtml} is true.
   * @deprecated Use {@link MapProvider.setInteractions} and `surface` instead.
   */
  popup?: string
  /**
   * When true, `popup` is inserted as HTML.
   * @deprecated Use {@link MapProvider.setInteractions} and `surface` instead.
   */
  popupHtml?: boolean
}

export type MarkerClusterIconStyle = 'default' | 'donut'

/** Defaults when {@link MarkerLayerOptions.cluster} is `true` or `{}`. */
export const MARKER_CLUSTER_DEFAULTS = {
  radius: 60,
  maxZoom: 14,
  icon: 'default' as MarkerClusterIconStyle,
} as const

/**
 * Tuning for clustered marker layers — same fields work on Leaflet, Mapbox GL, and Google Maps.
 * Providers map these internally (see {@link MARKER_CLUSTER_DEFAULTS}).
 */
export type MarkerClusterOptions = {
  /**
   * Max screen distance (px) to group markers into a cluster.
   * Leaflet → `maxClusterRadius`; Mapbox → `clusterRadius`; Google → SuperCluster `radius`.
   */
  radius?: number
  /**
   * Highest zoom level at which clusters still form; above this — individual markers.
   * Leaflet → `disableClusteringAtZoom`; Mapbox → `clusterMaxZoom`; Google → SuperCluster `maxZoom`.
   */
  maxZoom?: number
  /** Cluster bubble appearance. Default `'default'`. `'donut'` — proportional SVG ring by marker colors. */
  icon?: MarkerClusterIconStyle
  /**
   * Leaflet only — convex hull on cluster hover. Ignored by Mapbox / Google. Default false.
   */
  showCoverageOnHover?: boolean
  /** @deprecated use {@link MarkerClusterOptions.radius} */
  maxClusterRadius?: number
  /** @deprecated use {@link MarkerClusterOptions.radius} */
  clusterRadius?: number
  /** @deprecated use {@link MarkerClusterOptions.maxZoom} */
  clusterMaxZoom?: number
}

/**
 * Options for {@link MapLayer.setMarkers} / {@link MapLayer.upsertMarker}.
 * When `cluster` is true or an object, markers are clustered when zoomed out (GeoJSON + cluster in Mapbox; markercluster in Leaflet).
 */
export type MarkerArcLayerOptions = {
  /** Default true — draw arcs when markers carry {@link MapMarker.arc}. */
  enabled?: boolean
  /** Skip a segment when both resolved endpoints are closer than this (meters). Default ~50. */
  minSegmentMeters?: number
}

export type MarkerLayerOptions = {
  cluster?: boolean | MarkerClusterOptions
  arc?: MarkerArcLayerOptions
}

/** Circle appearance for a batched map point. Subset of {@link MapMarkerCircleStyle} dot fields. */
export type MapCloudPointStyle = {
  fillColor: string
  borderColor?: string
  radius?: number
  borderWidth?: number
}

/**
 * One point in a batched point-cloud layer.
 * App-specific fields live in `payload`; providers read position, style, and optional accuracy radius.
 */
export type MapCloudPoint = {
  position: LatLng
  style: MapCloudPointStyle
  /**
   * Geodesic accuracy radius in meters. `0`, missing, or non-finite values draw no ring.
   * Rings appear only when {@link MapPointCloudSetOptions.accuracyRings} asks for them.
   */
  accuracyRadiusMeters?: number
  /** Opaque hit data — forwarded to {@link MapInteractionsConfig.surface} via {@link hitPayload}. */
  payload?: Record<string, unknown>
}

/** High-count batched points (see {@link MapLayer.setPointCloud}). */
export type MapPointCloud = {
  points: readonly MapCloudPoint[]
}

/**
 * Zoom thinning for point-cloud paint (providers store the full set and thin for display).
 * Like marker clustering: a rendering concern, not app domain logic.
 */
export type MapPointCloudThinningOptions = {
  /** Spatial min-gap along array order (and per `payload.segmentId` run when present). */
  strategy: 'minGap'
  /**
   * `auto` — provider picks min gap meters from current zoom.
   * `false` — draw every point.
   */
  mode?: 'auto' | false
}

/**
 * `none` — no rings.
 * `all` — a ring under every visible point with a positive radius.
 * `on-click` — a ring under the last clicked visible point. Another point click moves it.
 */
export type MapPointCloudAccuracyRingsMode = 'none' | 'all' | 'on-click'

export type MapPointCloudSetOptions = {
  thinning?: false | MapPointCloudThinningOptions
  /**
   * Accuracy rings for this point cloud. Default `none`.
   * The app still owns one logical layer; the ring is an internal sublayer.
   */
  accuracyRings?: MapPointCloudAccuracyRingsMode
}

/**
 * One logical layer on the map: GeoJSON and/or markers. Obtain via {@link MapProvider.addLayer} or {@link MapProvider.getLayer}.
 * Provider-specific rendering stays inside the Leaflet / Mapbox implementation.
 */
export interface MapLayer {
  readonly id: string
  setGeoJSON(collection: GeoJsonFeatureCollection): void
  /**
   * Batch-rendered point cloud. Clears when `null`.
   * Leaflet: canvas batch. Mapbox / Google: GeoJSON circle points.
   * Optional thinning: providers keep the full set and re-thin on zoom.
   */
  setPointCloud(cloud: MapPointCloud | null, options?: MapPointCloudSetOptions): void
  /**
   * Replace all markers in this layer. Non-cluster: HTML markers. Cluster: replaces the store and re-syncs.
   * An empty array clears markers but keeps the layer (still hideable via {@link MapLayer.hide}).
   */
  setMarkers(markers: MapMarker[], options?: MarkerLayerOptions): void
  /** Add or replace one marker. For a new layer, `options` selects cluster mode when the layer is first created. */
  upsertMarker(marker: MapMarker, options?: MarkerLayerOptions): void
  removeMarker(markerId: string): void
  /** Clears GeoJSON and markers for this layer; the handle stays valid. */
  clear(): void
  show(): void
  hide(): void
}

/** Basemap mode for providers that support switching (e.g. streets vs satellite). */
export type MapBasemapMode = 'streets' | 'satellite'

export interface MapProvider {
  mount(container: HTMLElement): void | Promise<void>
  destroy(): void
  /**
   * Current basemap mode. Providers without basemap configuration return `'streets'`;
   * {@link setBasemapMode} is a no-op for them.
   */
  getBasemapMode(): MapBasemapMode
  /**
   * Switches basemap when the provider was created with per-mode configuration (e.g. tile URLs or style URLs).
   * No-op if the implementation does not support basemap switching.
   */
  setBasemapMode(mode: MapBasemapMode): void
  setCenter(center: LatLng, zoom?: number): void
  setZoom(zoom: number): void
  /** Current zoom level after mount; `undefined` if the map is not mounted yet. */
  getZoom(): number | undefined
  /** Current map center after mount; `undefined` if the map is not mounted yet. */
  getCenter(): LatLng | undefined
  /**
   * Call when the map container’s CSS size changes (flex, sidebars, splitters).
   * Leaflet: `invalidateSize`; Mapbox GL: `resize`; Google Maps: `resize` event.
   */
  notifyContainerResize(): void
  fitBounds(bounds: LatLngBounds): void
  /**
   * Registers a layer id and returns its handle. Idempotent: the same id returns the same handle until {@link MapProvider.removeLayer}.
   */
  addLayer(layerId: string): MapLayer
  getLayer(layerId: string): MapLayer | undefined
  getLayers(): MapLayer[]
  /** Removes all content for this id and invalidates the layer handle. */
  removeLayer(layerId: string): void
  /** Returns a function that removes this listener. */
  onClick(callback: (ev: { latlng: LatLng }) => void): () => void
  /** Returns a function that removes this listener. */
  onMoveEnd(callback: () => void): () => void
  /**
   * Map container pixel coordinates for overlay UI (Vue tooltips). Origin top-left of the mount element.
   */
  latLngToContainerPoint(latlng: LatLng): { x: number; y: number } | undefined
  /**
   * Registers unified hit handlers (click/hover) for markers, GeoJSON features, and clusters.
   * Pass `null` to clear. When set, static `popup` fields on markers/features are ignored.
   */
  setInteractions(config: MapInteractionsConfig | null): void
}
